import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Paperclip, Plus, Trash2, Pencil, Download, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { fmtDateTime } from "@/lib/format";
import { UOM_OPTIONS, INCOTERM_OPTIONS, InviteStatusBadge, ReviewStatusBadge, downloadFile, CATEGORY_OPTIONS } from "@/lib/enquiries";
import AuctionPanel from "@/components/AuctionPanel";
import ApprovalChainPanel from "@/components/ApprovalChainPanel";
import TermsEditor from "@/components/TermsEditor";

const EDIT_EMPTY_ADDR = { line1: "", line2: "", city: "", state: "", country: "India", postal_code: "", contact_name: "", contact_number: "", contact_email: "" };

const EMPTY_ITEM = { material_code: "", description: "", quantity: "", uom: "EA", incoterms: "", delivery_days: "" };

export default function EditEventDialog({ open, onOpenChange, enquiryId, data, reviews, initialTab = "details", onSaved }) {
  const enq = data?.enquiry || {};
  const invites = data?.invites || [];
  const attachments = data?.attachments || [];
  const participated = invites.filter((i) => i.status === "participated");
  const [tab, setTab] = useState(initialTab);
  const [saving, setSaving] = useState(false);
  const [details, setDetails] = useState({ description: "", incoterms: "EXW", delivery_days: "", title: "", buyer_comment: "", buyer_terms_conditions: "", delivery_address: { ...EDIT_EMPTY_ADDR } });
  const [timeline, setTimeline] = useState({ deadline: "", reason: "" });
  const [confirm, setConfirm] = useState(null); // {title, body, actionLabel, onConfirm}
  const [itemForm, setItemForm] = useState(null); // {mode:'add'|'edit', code, ...fields}
  const [showRemoved, setShowRemoved] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [addSupplierId, setAddSupplierId] = useState("");
  const [approvalNote, setApprovalNote] = useState("");
  const [approvalFiles, setApprovalFiles] = useState([]);
  const [uploadCat, setUploadCat] = useState("technical");
  const fileRef = useRef(null);
  const approvalFileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setTab(initialTab);
    setDetails({ description: enq.description || "", incoterms: enq.incoterms || "EXW", delivery_days: enq.delivery_days || "",
                 title: enq.title || "", buyer_comment: enq.buyer_comment || "",
                 buyer_terms_conditions: enq.buyer_terms_conditions || "",
                 delivery_address: { ...EDIT_EMPTY_ADDR, ...(enq.delivery_address || {}) } });
    setTimeline({ deadline: "", reason: "" });
    setItemForm(null);
    api.get("/suppliers").then((r) => setSuppliers(r.data.suppliers || r.data || [])).catch(() => {});
    api.get(`/enquiries/${enquiryId}/approval/attachments`).then((r) => setApprovalFiles(r.data.attachments || [])).catch(() => {});
  }, [open, initialTab, enquiryId]); // eslint-disable-line

  const saved = (msg) => { toast.success(msg); onSaved?.(); };

  const guard409 = async (err, retryPayload, retryFn) => {
    if (err?.response?.status === 409) {
      setConfirm({
        title: "Auction is configured / active",
        body: `${formatApiError(err)}`,
        actionLabel: "Close Auction & Apply",
        onConfirm: () => retryFn({ ...retryPayload, confirm_auction_close: true }),
      });
      return true;
    }
    toast.error(formatApiError(err));
    return true;
  };

  const saveDetails = async (force = false) => {
    const payload = {};
    if (details.description !== (enq.description || "")) payload.description = details.description;
    if (details.incoterms !== enq.incoterms) payload.incoterms = details.incoterms;
    if (Number(details.delivery_days) !== enq.delivery_days) payload.delivery_days = Number(details.delivery_days);
    if (details.title !== (enq.title || "")) payload.title = details.title;
    if (details.buyer_comment !== (enq.buyer_comment || "")) payload.buyer_comment = details.buyer_comment;
    if (details.buyer_terms_conditions !== (enq.buyer_terms_conditions || "")) payload.buyer_terms_conditions = details.buyer_terms_conditions;
    if (JSON.stringify(details.delivery_address) !== JSON.stringify({ ...EDIT_EMPTY_ADDR, ...(enq.delivery_address || {}) })) payload.delivery_address = details.delivery_address;
    if (!Object.keys(payload).length) return toast.error("No changes to save");
    const highImpact = ("incoterms" in payload || "delivery_days" in payload || "buyer_terms_conditions" in payload || "delivery_address" in payload) && participated.length > 0;
    if (highImpact && !force) {
      return setConfirm({
        title: "High-impact change",
        body: "This change may invalidate existing Seller quotations and technical evaluations. Affected parties will be notified and their responses marked for review.",
        actionLabel: "Save & Notify",
        onConfirm: () => saveDetails(true),
      });
    }
    setSaving(true);
    try {
      const r = await api.put(`/enquiries/${enquiryId}`, payload);
      saved(`Event updated — now at v${r.data.version}`);
    } catch (err) {
      await guard409(err, payload, (p) => api.put(`/enquiries/${enquiryId}`, p).then((r) => saved(`Event updated — now at v${r.data.version}`)).catch((e2) => toast.error(formatApiError(e2))));
    } finally { setSaving(false); setConfirm(null); }
  };

  const saveTimeline = async (force = false) => {
    if (!timeline.deadline) return toast.error("Pick a new deadline");
    if (!force) {
      return setConfirm({
        title: "Change event timer?",
        body: "Changing the event timer will affect Seller participation. All active sellers will be notified.",
        actionLabel: "Confirm Change",
        onConfirm: () => saveTimeline(true),
      });
    }
    setSaving(true);
    try {
      const r = await api.put(`/enquiries/${enquiryId}`, { deadline: new Date(timeline.deadline).toISOString(), deadline_reason: timeline.reason });
      saved(`Timer updated — now at v${r.data.version}`);
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); setConfirm(null); }
  };

  const itemPayload = (f, extra = {}) => ({
    material_code: f.material_code, description: f.description, quantity: Number(f.quantity), uom: f.uom,
    incoterms: f.incoterms || null, delivery_days: f.delivery_days ? Number(f.delivery_days) : null, ...extra,
  });

  const saveItem = async () => {
    const f = itemForm;
    if (!f.material_code.trim() || !f.description.trim() || !f.quantity) return toast.error("Material code, description and quantity are required");
    const payload = itemPayload(f);
    setSaving(true);
    try {
      if (f.mode === "add") {
        const r = await api.post(`/enquiries/${enquiryId}/items`, payload);
        saved(`Line item ${f.material_code} added — sellers notified (v${r.data.version})`);
      } else {
        const r = await api.put(`/enquiries/${enquiryId}/items/${encodeURIComponent(f.code)}`, payload);
        saved(`Line item updated (v${r.data.version})`);
      }
      setItemForm(null);
    } catch (err) {
      if (err?.response?.status === 409) {
        setConfirm({
          title: "Auction is configured / active", body: formatApiError(err), actionLabel: "Close Auction & Apply",
          onConfirm: async () => {
            try {
              const p = itemPayload(f, { confirm_auction_close: true });
              const r = f.mode === "add"
                ? await api.post(`/enquiries/${enquiryId}/items`, p)
                : await api.put(`/enquiries/${enquiryId}/items/${encodeURIComponent(f.code)}`, p);
              saved(`Saved (v${r.data.version})`); setItemForm(null);
            } catch (e2) { toast.error(formatApiError(e2)); } finally { setConfirm(null); }
          },
        });
      } else toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  const deleteItem = (it) => {
    setConfirm({
      title: `Delete line item ${it.material_code}?`,
      body: "Deleting this line item may affect existing seller responses, technical evaluations and commercial comparisons. Historical data (responses, reviews, audit history) will be retained and the item marked REMOVED. Continue?",
      actionLabel: "Delete Line Item",
      onConfirm: async () => {
        try {
          const r = await api.delete(`/enquiries/${enquiryId}/items/${encodeURIComponent(it.material_code)}`);
          saved(`Line item removed (v${r.data.version}) — historical data retained`);
        } catch (err) {
          if (err?.response?.status === 409) {
            setConfirm({
              title: "Auction is configured / active", body: formatApiError(err), actionLabel: "Close Auction & Delete",
              onConfirm: async () => {
                try {
                  const r = await api.delete(`/enquiries/${enquiryId}/items/${encodeURIComponent(it.material_code)}?confirm_auction_close=true`);
                  saved(`Line item removed (v${r.data.version})`);
                } catch (e2) { toast.error(formatApiError(e2)); } finally { setConfirm(null); }
              },
            });
            return;
          }
          toast.error(formatApiError(err));
        } finally { setConfirm(null); }
      },
    });
  };

  const addSupplier = async () => {
    if (!addSupplierId) return toast.error("Select a supplier");
    setSaving(true);
    try {
      const r = await api.post(`/enquiries/${enquiryId}/suppliers`, { supplier_id: addSupplierId });
      saved(`Supplier invited (v${r.data.version})`);
      setAddSupplierId("");
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const removeSupplier = (inv) => {
    setConfirm({
      title: `Remove ${inv.supplier_name}?`,
      body: "Removing this Seller will prevent further participation in the active Event. Existing responses and audit history will be retained.",
      actionLabel: "Remove Seller",
      onConfirm: async () => {
        try {
          const r = await api.delete(`/enquiries/${enquiryId}/suppliers/${inv.id}`);
          saved(`Seller removed (v${r.data.version})`);
        } catch (err) { toast.error(formatApiError(err)); } finally { setConfirm(null); }
      },
    });
  };

  const removeReview = async (rev) => {
    try {
      const r = await api.post(`/enquiries/${enquiryId}/reviews/${rev.id}/remove`);
      saved(`Reviewer assignment removed (v${r.data.version}) — historical review retained`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const uploadFile = async (e, approval = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      if (approval) {
        fd.append("note", approvalNote);
        await api.post(`/enquiries/${enquiryId}/approval/attachments`, fd);
        const r = await api.get(`/enquiries/${enquiryId}/approval/attachments`);
        setApprovalFiles(r.data.attachments || []);
        setApprovalNote("");
        toast.success("Approval attachment added — approvers can view it");
      } else {
        fd.append("category", uploadCat);
        await api.post(`/enquiries/${enquiryId}/attachments`, fd);
        toast.success("Attachment uploaded");
      }
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); } finally { e.target.value = ""; }
  };

  const items = enq.items || [];
  const activeItems = items.filter((i) => i.status !== "removed");
  const removedItems = items.filter((i) => i.status === "removed");
  const invitedRecordIds = new Set(invites.filter((i) => i.status !== "removed").map((i) => i.supplier_record_id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto bg-white sm:max-w-4xl" data-testid="edit-event-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit Event — {enq.enquiry_number} <span className="font-mono text-sm text-muted-foreground">v{enq.version || 1}</span></DialogTitle>
          <DialogDescription>Every material change creates a new Event Version, notifies affected parties and is recorded in the audit trail.</DialogDescription>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex h-auto flex-wrap justify-start" data-testid="edit-event-tabs">
            {[["details", "Event Details"], ["items", "Line Items"], ["suppliers", "Suppliers"], ["reviewers", "Technical Reviewers"],
              ["auction", "Auction"], ["approval", "Approval Flow"], ["attachments", "Attachments"], ["timeline", "Timeline"]]
              .filter(([k]) => enq.status !== "draft" || !["suppliers", "reviewers", "auction", "approval"].includes(k)).map(([k, l]) => (
              <TabsTrigger key={k} value={k} data-testid={`edit-tab-${k}`}>{l}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="details" className="space-y-4 pt-3" data-testid="edit-tab-content-details">
            <div><Label>Event Title</Label><Input data-testid="edit-title" className="mt-1.5" value={details.title} onChange={(e) => setDetails({ ...details, title: e.target.value })} /></div>
            <div><Label>Description / Buyer Instructions</Label><Textarea data-testid="edit-description" rows={3} className="mt-1.5" value={details.description} onChange={(e) => setDetails({ ...details, description: e.target.value })} /></div>
            <div>
              <Label>Buyer Terms &amp; Conditions * <span className="font-mono text-xs font-normal text-muted-foreground">(current v{enq.terms_version || 1} — editing creates a new Terms version, sellers must re-accept)</span></Label>
              <div className="mt-1.5"><TermsEditor value={details.buyer_terms_conditions} onChange={(v) => setDetails({ ...details, buyer_terms_conditions: v })} testid="edit-terms-editor" rows={5} /></div>
            </div>
            <div><Label>Buyer Comment (Optional)</Label><Textarea data-testid="edit-buyer-comment" rows={2} className="mt-1.5" value={details.buyer_comment} onChange={(e) => setDetails({ ...details, buyer_comment: e.target.value })} /></div>
            <div>
              <Label>Delivery Address *</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-3">
                <Input data-testid="edit-addr-line1" placeholder="Address Line 1 *" value={details.delivery_address.line1} onChange={(e) => setDetails({ ...details, delivery_address: { ...details.delivery_address, line1: e.target.value } })} />
                <Input data-testid="edit-addr-line2" placeholder="Address Line 2" value={details.delivery_address.line2} onChange={(e) => setDetails({ ...details, delivery_address: { ...details.delivery_address, line2: e.target.value } })} />
                <Input data-testid="edit-addr-city" placeholder="City *" value={details.delivery_address.city} onChange={(e) => setDetails({ ...details, delivery_address: { ...details.delivery_address, city: e.target.value } })} />
                <Input data-testid="edit-addr-state" placeholder="State *" value={details.delivery_address.state} onChange={(e) => setDetails({ ...details, delivery_address: { ...details.delivery_address, state: e.target.value } })} />
                <Input data-testid="edit-addr-country" placeholder="Country *" value={details.delivery_address.country} onChange={(e) => setDetails({ ...details, delivery_address: { ...details.delivery_address, country: e.target.value } })} />
                <Input data-testid="edit-addr-postal" placeholder="PIN / Postal Code *" value={details.delivery_address.postal_code} onChange={(e) => setDetails({ ...details, delivery_address: { ...details.delivery_address, postal_code: e.target.value } })} />
                <Input data-testid="edit-addr-contact-name" placeholder="Delivery Contact Person" value={details.delivery_address.contact_name} onChange={(e) => setDetails({ ...details, delivery_address: { ...details.delivery_address, contact_name: e.target.value } })} />
                <Input data-testid="edit-addr-contact-number" placeholder="Contact Number" value={details.delivery_address.contact_number} onChange={(e) => setDetails({ ...details, delivery_address: { ...details.delivery_address, contact_number: e.target.value } })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Incoterms</Label>
                <Select value={details.incoterms} onValueChange={(v) => setDetails({ ...details, incoterms: v })}>
                  <SelectTrigger data-testid="edit-incoterms" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{INCOTERM_OPTIONS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Required Delivery Days</Label><Input data-testid="edit-delivery-days" type="number" min="1" className="mt-1.5" value={details.delivery_days} onChange={(e) => setDetails({ ...details, delivery_days: e.target.value })} /></div>
            </div>
            {(details.incoterms !== enq.incoterms || Number(details.delivery_days) !== enq.delivery_days) && participated.length > 0 && (
              <p className="flex items-start gap-2 rounded-md border border-[#FDE68A] bg-[#FEF3C7]/50 px-3 py-2 text-xs text-[#92400E]" data-testid="edit-impact-warning">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> High-impact change — {participated.length} seller response(s) will be marked RESPONSE REQUIRES REVIEW.
              </p>
            )}
          </TabsContent>

          <TabsContent value="items" className="pt-3" data-testid="edit-tab-content-items">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs text-muted-foreground">{activeItems.length} active · {removedItems.length} removed</p>
              <div className="flex gap-2">
                {removedItems.length > 0 && (
                  <Button variant="outline" size="sm" data-testid="toggle-removed-items" onClick={() => setShowRemoved(!showRemoved)}>
                    {showRemoved ? "Hide Removed Items" : "Show Removed Items"}
                  </Button>
                )}
                <Button size="sm" data-testid="add-line-item-btn" onClick={() => setItemForm({ mode: "add", ...EMPTY_ITEM })} className="bg-slate-900 hover:bg-slate-800">
                  <Plus className="mr-1 h-3.5 w-3.5" /> Add Line Item
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm" data-testid="edit-items-table">
                <thead><tr className="border-b bg-slate-50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Material Code</th><th className="px-3 py-2 font-medium">Description</th>
                  <th className="px-3 py-2 font-medium text-right">Qty</th><th className="px-3 py-2 font-medium">UOM</th>
                  <th className="px-3 py-2 font-medium">Incoterms</th><th className="px-3 py-2 font-medium text-right">Delivery Days</th>
                  <th className="px-3 py-2 font-medium text-right">Actions</th>
                </tr></thead>
                <tbody>
                  {(showRemoved ? items : activeItems).map((it) => (
                    <tr key={it.id} data-testid={`edit-item-row-${it.material_code}`} className={`border-b last:border-0 ${it.status === "removed" ? "bg-slate-50 text-slate-400 line-through" : ""}`}>
                      <td className="px-3 py-2 font-mono text-xs font-semibold">{it.material_code}</td>
                      <td className="max-w-56 truncate px-3 py-2">{it.description}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{it.quantity}</td>
                      <td className="px-3 py-2 font-mono text-xs">{it.uom}</td>
                      <td className="px-3 py-2 font-mono text-xs">{it.incoterms || enq.incoterms}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{it.delivery_days || enq.delivery_days}</td>
                      <td className="px-3 py-2 text-right">
                        {it.status === "removed"
                          ? <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase no-underline" data-testid={`item-removed-badge-${it.material_code}`}>Removed</span>
                          : (<span className="inline-flex gap-1">
                              <Button variant="ghost" size="icon" data-testid={`edit-item-${it.material_code}`} onClick={() => setItemForm({ mode: "edit", code: it.material_code, material_code: it.material_code, description: it.description, quantity: it.quantity, uom: it.uom, incoterms: it.incoterms || "", delivery_days: it.delivery_days || "" })}><Pencil className="h-3.5 w-3.5" /></Button>
                              <Button variant="ghost" size="icon" data-testid={`delete-item-${it.material_code}`} onClick={() => deleteItem(it)}><Trash2 className="h-3.5 w-3.5 text-red-600" /></Button>
                            </span>)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {itemForm && (
              <div className="mt-3 space-y-3 rounded-md border border-slate-300 bg-slate-50 p-3" data-testid="item-form">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{itemForm.mode === "add" ? "Add Line Item" : `Edit ${itemForm.code}`}</p>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                  <div><Label className="text-xs">Material Code *</Label><Input data-testid="item-material-code" className="mt-1 bg-white" value={itemForm.material_code} onChange={(e) => setItemForm({ ...itemForm, material_code: e.target.value })} /></div>
                  <div className="col-span-2"><Label className="text-xs">Description *</Label><Input data-testid="item-description" className="mt-1 bg-white" value={itemForm.description} onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })} /></div>
                  <div><Label className="text-xs">Quantity *</Label><Input data-testid="item-quantity" type="number" min="0" step="any" className="mt-1 bg-white" value={itemForm.quantity} onChange={(e) => setItemForm({ ...itemForm, quantity: e.target.value })} /></div>
                  <div><Label className="text-xs">UOM *</Label>
                    <Select value={itemForm.uom} onValueChange={(v) => setItemForm({ ...itemForm, uom: v })}>
                      <SelectTrigger data-testid="item-uom" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">{UOM_OPTIONS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Incoterms (opt.)</Label>
                    <Select value={itemForm.incoterms || "event"} onValueChange={(v) => setItemForm({ ...itemForm, incoterms: v === "event" ? "" : v })}>
                      <SelectTrigger data-testid="item-incoterms" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white"><SelectItem value="event">Event default</SelectItem>{INCOTERM_OPTIONS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Delivery Days (opt.)</Label><Input data-testid="item-delivery-days" type="number" min="1" className="mt-1 bg-white" placeholder={String(enq.delivery_days)} value={itemForm.delivery_days} onChange={(e) => setItemForm({ ...itemForm, delivery_days: e.target.value })} /></div>
                  <div><Label className="text-xs">Category (opt.)</Label>
                    <Select value={itemForm.category || "none"} onValueChange={(v) => setItemForm({ ...itemForm, category: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="item-category" className="mt-1 bg-white"><SelectValue placeholder="Auto-detect" /></SelectTrigger>
                      <SelectContent className="bg-white"><SelectItem value="none">Auto-detect</SelectItem>{CATEGORY_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" data-testid="item-form-cancel" onClick={() => setItemForm(null)}>Cancel</Button>
                  <Button size="sm" data-testid="item-form-save" disabled={saving} onClick={saveItem} className="bg-slate-900 hover:bg-slate-800">
                    {saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} {itemForm.mode === "add" ? "Add Item" : "Save Item"}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="suppliers" className="pt-3" data-testid="edit-tab-content-suppliers">
            <div className="mb-3 flex items-center gap-2">
              <Select value={addSupplierId} onValueChange={setAddSupplierId}>
                <SelectTrigger data-testid="add-supplier-select" className="max-w-sm"><SelectValue placeholder="Select supplier to invite" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {suppliers.filter((s) => !invitedRecordIds.has(s.id)).map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`add-supplier-${s.id.slice(0, 8)}`}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" data-testid="add-supplier-btn" disabled={saving} onClick={addSupplier} className="bg-slate-900 hover:bg-slate-800">
                <Plus className="mr-1 h-3.5 w-3.5" /> Add Supplier
              </Button>
            </div>
            <div className="divide-y rounded-md border" data-testid="edit-suppliers-list">
              {invites.map((inv) => (
                <div key={inv.id} className="flex items-center gap-3 px-3 py-2.5" data-testid={`edit-supplier-${inv.supplier_name.replace(/\W+/g, "-").toLowerCase()}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{inv.supplier_name}</p>
                    <p className="text-xs text-muted-foreground">{inv.supplier_email}</p>
                  </div>
                  <InviteStatusBadge status={inv.status} />
                  {inv.status !== "removed" && (
                    <Button variant="outline" size="sm" data-testid={`remove-supplier-${inv.id.slice(0, 8)}`} onClick={() => removeSupplier(inv)}
                      className="border-red-200 text-red-700 hover:bg-red-50">Remove</Button>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="reviewers" className="pt-3" data-testid="edit-tab-content-reviewers">
            <p className="mb-3 text-xs text-muted-foreground">Assign new reviewers via "Send for Technical Review" in the Technical Evaluation section. Removing an assignment preserves the historical review.</p>
            <div className="divide-y rounded-md border" data-testid="edit-reviewers-list">
              {(reviews || []).length === 0 && <p className="px-3 py-4 text-sm text-muted-foreground">No reviewer assignments yet.</p>}
              {(reviews || []).map((r) => (
                <div key={r.id} className="flex items-center gap-3 px-3 py-2.5" data-testid={`edit-review-${r.id.slice(0, 8)}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{r.reviewer_name} <span className="text-xs font-normal text-muted-foreground">→ {r.supplier_name}</span></p>
                    {r.reviewer_comment && <p className="truncate text-xs text-muted-foreground">"{r.reviewer_comment}"</p>}
                  </div>
                  <ReviewStatusBadge status={r.status} displayStatus={r.display_status} />
                  {r.status !== "removed" && (
                    <Button variant="outline" size="sm" data-testid={`remove-review-${r.id.slice(0, 8)}`} onClick={() => removeReview(r)}
                      className="border-red-200 text-red-700 hover:bg-red-50">Remove</Button>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="auction" className="pt-3" data-testid="edit-tab-content-auction">
            <AuctionPanel enquiryId={enquiryId} />
          </TabsContent>

          <TabsContent value="approval" className="pt-3" data-testid="edit-tab-content-approval">
            <ApprovalChainPanel enquiryId={enquiryId} invites={invites} />
          </TabsContent>

          <TabsContent value="attachments" className="space-y-4 pt-3" data-testid="edit-tab-content-attachments">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Event Attachments</p>
              <div className="flex flex-wrap items-center gap-2">
                {attachments.map((f) => (
                  <button key={f.id} data-testid={`edit-attachment-${f.id.slice(0, 8)}`} onClick={() => downloadFile(`/enquiry-files/${f.id}/download`, f.filename)}
                    className="flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs hover:bg-slate-50">
                    <Paperclip className="h-3 w-3" /> {f.filename}
                  </button>
                ))}
                <input ref={fileRef} type="file" className="hidden" data-testid="edit-attachment-input" onChange={(e) => uploadFile(e, false)} />
                <Select value={uploadCat} onValueChange={setUploadCat}>
                  <SelectTrigger data-testid="edit-upload-category" className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="technical">Technical</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" data-testid="edit-upload-btn" onClick={() => fileRef.current?.click()}><Paperclip className="mr-1.5 h-3.5 w-3.5" /> Upload</Button>
              </div>
            </div>
            <div className="rounded-md border border-[#BFDBFE] bg-[#DBEAFE]/20 p-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#1E40AF]">Approval Documents (shared with approvers)</p>
              <div className="flex flex-wrap items-center gap-2">
                {approvalFiles.map((f) => (
                  <button key={f.id} data-testid={`approval-attachment-${f.id.slice(0, 8)}`} onClick={() => downloadFile(`/enquiry-files/${f.id}/download`, f.filename)}
                    className="flex items-center gap-1.5 rounded-md border bg-white px-2.5 py-1 text-xs hover:bg-slate-50">
                    <Download className="h-3 w-3" /> {f.filename}{f.note ? ` — ${f.note}` : ""}
                  </button>
                ))}
                <Input data-testid="approval-attachment-note" className="h-8 w-56 bg-white text-xs" placeholder="Note (e.g. Commercial Justification)" value={approvalNote} onChange={(e) => setApprovalNote(e.target.value)} />
                <input ref={approvalFileRef} type="file" className="hidden" data-testid="approval-attachment-input" onChange={(e) => uploadFile(e, true)} />
                <Button variant="outline" size="sm" data-testid="add-approval-attachment-btn" onClick={() => approvalFileRef.current?.click()}>
                  <Plus className="mr-1 h-3.5 w-3.5" /> Add Attachment
                </Button>
              </div>
              <p className="mt-1.5 text-[11px] text-muted-foreground">Optional — approval can proceed without attachments.</p>
            </div>
          </TabsContent>

          <TabsContent value="timeline" className="space-y-4 pt-3" data-testid="edit-tab-content-timeline">
            <p className="text-sm text-muted-foreground">Current deadline: <span className="font-medium text-slate-900" data-testid="edit-current-deadline">{fmtDateTime(enq.deadline)}</span></p>
            <div><Label>New Deadline *</Label><Input data-testid="edit-deadline-input" type="datetime-local" className="mt-1.5" value={timeline.deadline} onChange={(e) => setTimeline({ ...timeline, deadline: e.target.value })} /></div>
            <div><Label>Reason (recorded in audit trail)</Label><Input data-testid="edit-deadline-reason" className="mt-1.5" placeholder="e.g. Sellers requested more time" value={timeline.reason} onChange={(e) => setTimeline({ ...timeline, reason: e.target.value })} /></div>
            <Button size="sm" data-testid="edit-deadline-save" disabled={saving} onClick={() => saveTimeline(false)} className="bg-slate-900 hover:bg-slate-800">
              {saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} Update Timer
            </Button>
          </TabsContent>
        </Tabs>
        <DialogFooter className="mt-4">
          <Button variant="outline" data-testid="edit-event-cancel" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="edit-event-save" disabled={saving} onClick={() => saveDetails(false)} className="bg-slate-900 hover:bg-slate-800">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save Changes
          </Button>
        </DialogFooter>

        <Dialog open={!!confirm} onOpenChange={() => setConfirm(null)}>
          <DialogContent className="bg-white" data-testid="edit-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">{confirm?.title}</DialogTitle>
              <DialogDescription>{confirm?.body}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" data-testid="edit-confirm-cancel" onClick={() => setConfirm(null)}>Cancel</Button>
              <Button data-testid="edit-confirm-submit" disabled={saving} onClick={confirm?.onConfirm} className="bg-slate-900 hover:bg-slate-800">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {confirm?.actionLabel}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
